<script>import {
  getScrollableParent,
  useInfiniteScroll
} from "@inertiajs/core";
import { onDestroy, onMount } from "svelte";
export let data;
export let buffer = 0;
export let as = "div";
export let manual = false;
export let manualAfter = 0;
export let preserveUrl = false;
export let reverse = false;
export let autoScroll = void 0;
export let startElement = null;
export let endElement = null;
export let itemsElement = null;
export let onlyNext = false;
export let onlyPrevious = false;
let itemsElementRef;
let startElementRef;
let endElementRef;
let loadingPrevious = false;
let loadingNext = false;
let requestCount = 0;
let hasPreviousPage = false;
let hasNextPage = false;
$: resolvedItemsElement = resolveHTMLElement(itemsElement, itemsElementRef);
$: scrollableParent = resolvedItemsElement ? getScrollableParent(resolvedItemsElement) : null;
$: sharedExposed = {
  loadingPrevious,
  loadingNext,
  hasPrevious: hasPreviousPage,
  hasNext: hasNextPage
};
$: exposedPrevious = {
  loading: loadingPrevious,
  fetch: fetchPrevious,
  autoMode: headerAutoMode,
  manualMode: !headerAutoMode,
  hasMore: hasPreviousPage,
  ...sharedExposed
};
$: exposedNext = {
  loading: loadingNext,
  fetch: fetchNext,
  autoMode: footerAutoMode,
  manualMode: !footerAutoMode,
  hasMore: hasNextPage,
  ...sharedExposed
};
$: exposedSlot = {
  loading: loadingPrevious || loadingNext,
  loadingPrevious,
  loadingNext
};
let infiniteScrollInstance = null;
function resolveHTMLElement(value, fallback) {
  if (!value) {
    return fallback;
  }
  if (typeof value === "string") {
    return document.querySelector(value);
  }
  if (typeof value === "function") {
    return value() || null;
  }
  return fallback;
}
function scrollToBottom() {
  if (scrollableParent) {
    scrollableParent.scrollTo({
      top: scrollableParent.scrollHeight,
      behavior: "instant"
    });
  } else {
    window.scrollTo({
      top: document.body.scrollHeight,
      behavior: "instant"
    });
  }
}
export function fetchPrevious(options) {
  infiniteScrollInstance?.dataManager.fetchPrevious(options);
}
export function fetchNext(options) {
  infiniteScrollInstance?.dataManager.fetchNext(options);
}
export function hasPrevious() {
  return infiniteScrollInstance?.dataManager.hasPrevious() || false;
}
export function hasNext() {
  return infiniteScrollInstance?.dataManager.hasNext() || false;
}
onMount(() => {
  setTimeout(setupInfiniteScrollInstance);
});
function syncStateFromDataManager() {
  requestCount = infiniteScrollInstance.dataManager.getRequestCount();
  hasPreviousPage = infiniteScrollInstance.dataManager.hasPrevious();
  hasNextPage = infiniteScrollInstance.dataManager.hasNext();
}
function setupInfiniteScrollInstance() {
  const resolvedItemsElement2 = resolveHTMLElement(itemsElement, itemsElementRef);
  const resolvedStartElement = resolveHTMLElement(startElement, startElementRef);
  const resolvedEndElement = resolveHTMLElement(endElement, endElementRef);
  infiniteScrollInstance = useInfiniteScroll({
    // Data
    getPropName: () => data,
    inReverseMode: () => reverse ?? false,
    shouldFetchNext: () => !onlyPrevious,
    shouldFetchPrevious: () => !onlyNext,
    shouldPreserveUrl: () => preserveUrl ?? false,
    // Elements
    getTriggerMargin: () => buffer ?? 0,
    getStartElement: () => resolvedStartElement,
    getEndElement: () => resolvedEndElement,
    getItemsElement: () => resolvedItemsElement2,
    getScrollableParent: () => resolvedItemsElement2 ? getScrollableParent(resolvedItemsElement2) : null,
    // Request callbacks
    onBeforePreviousRequest: () => loadingPrevious = true,
    onBeforeNextRequest: () => loadingNext = true,
    onCompletePreviousRequest: () => {
      loadingPrevious = false;
      syncStateFromDataManager();
    },
    onCompleteNextRequest: () => {
      loadingNext = false;
      syncStateFromDataManager();
    },
    onDataReset: syncStateFromDataManager
  });
  const { dataManager, elementManager } = infiniteScrollInstance;
  syncStateFromDataManager();
  elementManager.setupObservers();
  elementManager.processServerLoadedElements(dataManager.getLastLoadedPage());
  const shouldAutoScroll = autoScroll !== void 0 ? autoScroll : reverse;
  if (shouldAutoScroll) {
    scrollToBottom();
  }
}
$: manualMode = manual || manualAfter !== void 0 && manualAfter > 0 && requestCount >= manualAfter;
$: autoLoad = !manualMode;
$: headerAutoMode = autoLoad && !onlyNext;
$: footerAutoMode = autoLoad && !onlyPrevious;
$: {
  ;
  [autoLoad, onlyNext, onlyPrevious, reverse];
  autoLoad ? infiniteScrollInstance?.elementManager.enableTriggers() : infiniteScrollInstance?.elementManager.disableTriggers();
}
onDestroy(() => infiniteScrollInstance?.flush());
</script>

{#if !startElement && !reverse}
  <div bind:this={startElementRef}>
    <slot name="previous" {exposedPrevious} {...exposedPrevious}>
      {#if loadingPrevious}
        <slot name="loading" {exposedPrevious} {...exposedPrevious} />
      {/if}
    </slot>
  </div>
{/if}

{#if !endElement && reverse}
  <div bind:this={endElementRef}>
    <slot name="next" {exposedNext} {...exposedNext}>
      {#if loadingNext}
        <slot name="loading" {exposedNext} {...exposedNext} />
      {/if}
    </slot>
  </div>
{/if}

<svelte:element this={as} bind:this={itemsElementRef} {...$$restProps}>
  <slot {exposedSlot} {...exposedSlot} />
</svelte:element>

{#if !startElement && reverse}
  <div bind:this={startElementRef}>
    <slot name="previous" {exposedPrevious} {...exposedPrevious}>
      {#if loadingPrevious}
        <slot name="loading" {exposedPrevious} {...exposedPrevious} />
      {/if}
    </slot>
  </div>
{/if}

{#if !endElement && !reverse}
  <div bind:this={endElementRef}>
    <slot name="next" {exposedNext} {...exposedNext}>
      {#if loadingNext}
        <slot name="loading" {exposedNext} {...exposedNext} />
      {/if}
    </slot>
  </div>
{/if}
